import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Landing.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import QuotesTicker from './QuotesTicker';
import { DerivAffiliateBanner } from './DerivAffiliate';

function Landing() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`, { replace: true });
    }
  }, [location.search, navigate]);

  return (
    <HelmetProvider>
      <div className="landing page-shell">
        <Helmet>
          <title>Vix Algo Trading Store - Algorithmic Trading, Refined</title>
          <meta
            name="description"
            content="Discover disciplined algorithmic trading tools. Expert Advisors, indicators, and source code with professional support and clear licensing."
          />
        </Helmet>

        {/* Above the hero: live quotes are a signal that the site is connected to real markets,
            which is worth more before someone has read anything than after. */}
        <QuotesTicker />

        <section className="landing-hero">
          <div className="landing-hero-content">
            <span className="tag">Built for MT5</span>
            <h1 className="landing-title">Professional tools for systematic traders.</h1>
            <p className="landing-lead">
              Build your edge with curated Expert Advisors, precision indicators, and source code packs.
              Transparent licensing, instant delivery, and clear product management from one place.
            </p>
            <div className="landing-actions">
              <Link to="/products" className="primary-cta">Explore Products</Link>
              <Link to="/tester" className="ghost-cta">Open Builder</Link>
              <Link to="/tester?panel=ai" className="ghost-cta">VixAI</Link>
              <Link to="/community" className="ghost-cta">Community</Link>
              <Link to="/tester-premium" className="ghost-cta">VixPremium</Link>
              <Link to="/contact-us" className="ghost-cta">Talk to Support</Link>
            </div>
            <div className="landing-proof">
              <div className="proof-card">
                <p className="proof-title">Signal Discipline</p>
                <p className="proof-text">Risk-aware entries, structured exits, and clean documentation.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">Instant Access</p>
                <p className="proof-text">Get files immediately after purchase with secure delivery.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">Strategy Builder</p>
                <p className="proof-text">Prototype, save, export, and validate strategy logic directly in the browser.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">Cloud Signals</p>
                <p className="proof-text">Run a strategy — or an MQL5 EA you already own — live on our servers, with alerts when it trades.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">Copy to MetaTrader</p>
                <p className="proof-text">Mirror your signals into your own MT5 account with one EA, and track that account from anywhere.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">VixAI</p>
                <p className="proof-text">Turn custom strategy ideas into controlled inputs, rules, visuals, and panel specifications.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">Premium Community</p>
                <p className="proof-text">Share strategy threads, reports, files, and public presets with other VixPremium members.</p>
              </div>
              <div className="proof-card">
                <p className="proof-title">License Clarity</p>
                <p className="proof-text">Per-product licensing with straightforward account and support handling.</p>
              </div>
            </div>
          </div>
          <div className="landing-hero-visual">
            <div className="visual-card">
              <div className="visual-top">
                <span className="visual-chip">Strategy Stack</span>
                <span className="visual-chip">Builder Beta</span>
                <span className="visual-chip warm">Release Notes</span>
              </div>
              <h2>Build, inspect, and test the idea before it becomes an EA.</h2>
              <p>
                Open VixAlgoBuilder to prototype rules visually, watch the chart react, and review the trade flow before deeper MT5 validation.
              </p>
              <div className="visual-metrics">
                <div>
                  <p className="metric-value">Chart</p>
                  <p className="metric-label">Rendered live</p>
                </div>
                <div>
                  <p className="metric-value">Rules</p>
                  <p className="metric-label">Visual builder</p>
                </div>
                <div>
                  <p className="metric-value">PnL</p>
                  <p className="metric-label">Tracked instantly</p>
                </div>
              </div>
            </div>
            <div className="glow-panel"></div>
          </div>
        </section>

        <DerivAffiliateBanner variant="default" />

        <section className="landing-sections">
          <div className="section-card">
            <h3>What you get</h3>
            <ul className="landing-list">
              <li>Ready-to-run Expert Advisors with documented logic.</li>
              <li>Indicators and source code for custom strategies.</li>
              <li>Set files, backtest assets, and versioned updates.</li>
            </ul>
          </div>
          <div className="section-card">
            <h3>Why Us?</h3>
            <ul className="landing-list">
              <li>Each product is expertly reviewed for risk control and usability.</li>
              <li>Transparent pricing and license terms.</li>
              <li>Support that responds within 24-48 hours.</li>
            </ul>
          </div>
          <div className="section-card">
            <h3>Next steps</h3>
            <p className="muted-text">
              Browse the catalog, read release notes, or open the builder to prototype a ruleset before you buy or build.
            </p>
            <div className="landing-actions">
              <Link to="/tester" className="primary-cta compact">Launch Builder</Link>
              <Link to="/tester?panel=ai" className="ghost-cta compact">VixAI</Link>
              <Link to="/builder-docs" className="ghost-cta compact">Read Docs</Link>
              <Link to="/tester-premium" className="ghost-cta compact">See VixPremium</Link>
              <Link to="/products" className="ghost-cta compact">Go to Catalog</Link>
            </div>
          </div>
        </section>

        <section className="landing-cta">
          <div>
            <h2>Ready to build your edge?</h2>
            <p className="muted-text">
              Create an account to manage licenses, downloads, and support conversations in one place.
            </p>
          </div>
          <div className="landing-actions">
            <Link to="/signup" className="primary-cta">Create Account</Link>
            <Link to="/products" className="ghost-cta">Browse Products</Link>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
}

export default Landing;

